import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { TrendingDown, Clock, DollarSign, ArrowRight } from 'lucide-react';

import { openCustomerPortal, PORTAL_BASE_URL } from '../../../utils/cn';
import LazyVideo from '../../../components/LazyVideo';

// "The Future of Roofing is Here" split-screen section (2026-05-23).
// Sits directly below the hero on the homepage, before ZeroSection.
//
// Pass 2 — animation + effects (this file's current state):
//   • Single parent observer on the <motion.section> fires once when 15%
//     of the section is in view. All animated descendants inherit the
//     hidden → visible state change.
//   • Left column builds in reading order with absolute per-element
//     delays (variants with `delay` in transition, NOT staggerChildren —
//     so each child has independent timing without parent orchestration
//     of stagger). Eyebrow → headline → subhead → 3 proof points (each
//     cascading text + spring-scale icon) → CTA.
//   • Tablet entrance runs ALONGSIDE the left build, not after. It
//     starts at t=0.10 (just after the eyebrow) with a fade + scale
//     0.92→1 + rotateY 12°→0 + rotateX 4°→0 — a restrained "twist into
//     place" that takes ~0.9s and settles by the time the CTA fades in.
//     Reads as one orchestrated arrival, not two separate animations.
//   • After entrance settles, the tablet's box-shadow cycles a soft
//     cyan glow at a 4s cadence (10% → 30% → 10% alpha). delay=1.10s
//     on the boxShadow-only transition holds it at REST until the
//     entrance completes, then starts the infinite loop.
//   • Section has `overflow-hidden` to clip any 3D bleed during the
//     tilt entrance. Once settled at rotateY:0, nothing overflows.
//   • The wireframe video continues playing through the entire entrance —
//     the transform applies to the tablet wrapper, so the video tilts
//     WITH the tablet (no distortion, just an angle until the tilt
//     settles to flat). autoPlay/muted/playsInline + no `loop` + poster
//     = last-frame freeze, same as the prior pass.
//
// Mobile order: COPY FIRST (eyebrow → CTA), TABLET BELOW. The 3D tilt
// runs on mobile too — modern browsers handle video + 3D transforms
// without flicker. If perf issues surface on a specific device, the
// brief's fallback path is to drop the rotateY/rotateX and keep only
// the fade + scale (single-line change in tabletVariants below).

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];

// Tablet shadows — restacked 2026-05-24 for a FLOATING / suspended
// product-photo feel. Three slate-dark layers (close 8px → mid 24px →
// deep 50px) give the impression of the device hovering well above the
// white surface, not resting on it. Cyan ambient halo stays as the
// brand-color glow. Difference between REST and PEAK is only the cyan
// halo's blur (60→100px) and alpha (0.12→0.45) — the slate elevation
// stack is identical so only the cyan layer "breathes," and the float
// shadow remains constant beneath the device through the drift cycle.
const TABLET_REST_SHADOW =
  '0 50px 100px rgba(15, 23, 42, 0.22),' +
  ' 0 24px 48px rgba(15, 23, 42, 0.15),' +
  ' 0 8px 16px rgba(15, 23, 42, 0.10),' +
  ' 0 0 60px rgba(7, 176, 233, 0.12),' +
  ' inset 0 0 0 1px rgba(255, 255, 255, 0.05)';

const TABLET_PEAK_SHADOW =
  '0 50px 100px rgba(15, 23, 42, 0.22),' +
  ' 0 24px 48px rgba(15, 23, 42, 0.15),' +
  ' 0 8px 16px rgba(15, 23, 42, 0.10),' +
  ' 0 0 100px rgba(7, 176, 233, 0.45),' +
  ' inset 0 0 0 1px rgba(255, 255, 255, 0.05)';

// ─── Variants ───────────────────────────────────────────────────────────

// Empty wrapper variants — just for state propagation. Outer
// motion.section uses these to send `hidden`/`visible` to descendants.
const sectionContainerV = {
  hidden: {},
  visible: {},
};

const eyebrowV = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, delay: 0, ease: EASE_OUT_EXPO } },
};

const headlineV = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.08, ease: EASE_OUT_EXPO } },
};

const subheadV = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, delay: 0.18, ease: EASE_OUT_EXPO } },
};

// ─── Proof-point motion vocabulary ──────────────────────────────────────
// Three layered effects per row:
//   1. ROW slide-in (proofItemV) — bigger horizontal travel than v1
//      (-28px vs -12px) so the entrance carries more presence.
//   2. ICON-BOX scale + rotate (proofIconV) — spring-driven, scale 0→1,
//      rotate -90°→0°. Gives each icon a "twist-and-pop" arrival.
//      Box-shadow keyframes are appended as an infinite-repeat breath
//      cycle that fires AFTER the entrance settles — 3.5s cadence,
//      phase-staggered by 0.8s per row so the three icons create a
//      rolling cyan-pulse ripple rather than blinking in unison.
//   3. SONAR-PING ring (proofPingV) — a one-shot expanding cyan ring
//      fires from each icon-box at the moment the icon arrives, then
//      fades. Lives as an absolute overlay over the icon-box.

// Icon-box shadows — rest is the established inset cyan ring; peak adds
// an outer cyan glow during the breath phase. Same vocabulary as the
// tablet's REST/PEAK shadows, scaled down for the small icon boxes.
const ICON_REST_SHADOW = 'inset 0 0 0 1px rgba(7, 176, 233, 0.18)';
const ICON_PEAK_SHADOW =
  'inset 0 0 0 1px rgba(7, 176, 233, 0.45),' +
  ' 0 0 18px rgba(7, 176, 233, 0.40)';

// Proof points cascade from left. `custom` is the row index.
const proofItemV = {
  hidden: { opacity: 0, x: -28 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.55, delay: 0.30 + i * 0.12, ease: EASE_OUT_EXPO },
  }),
};

// Icon flourish — scale 0→1 + rotate -90°→0° with spring overshoot.
// After entrance settles, an infinite box-shadow breath cycle starts
// (3.5s duration, phase-staggered per row via per-row delay calculation
// below). `custom` is the row index.
const proofIconV = {
  hidden: { scale: 0, rotate: -90, boxShadow: ICON_REST_SHADOW },
  visible: (i) => ({
    scale: 1,
    rotate: 0,
    boxShadow: [ICON_REST_SHADOW, ICON_PEAK_SHADOW, ICON_REST_SHADOW],
    transition: {
      scale:  { delay: 0.42 + i * 0.12, type: 'spring', stiffness: 280, damping: 14 },
      rotate: { delay: 0.42 + i * 0.12, type: 'spring', stiffness: 280, damping: 14 },
      // Breath cycle — starts at 1.7s + (row × 0.8s) so the three rows
      // peak at offset times, creating a rolling ripple rather than a
      // unison blink. 3.5s duration is fast enough to be perceptibly
      // "alive" without being distracting.
      boxShadow: {
        delay: 1.7 + i * 0.8,
        duration: 3.5,
        repeat: Infinity,
        ease: 'easeInOut',
        times: [0, 0.5, 1],
      },
    },
  }),
};

// Sonar-ping ring — one-shot expanding cyan ring that fires from each
// icon-box at the moment the icon arrives. Opacity goes 0 → 0.75 → 0
// (briefly visible then fades), scale goes 0.5 → 1.9 (expands outward).
// Lives as an absolute overlay inside the icon-box wrapper.
const proofPingV = {
  hidden: { scale: 0.5, opacity: 0 },
  visible: (i) => ({
    scale: [0.5, 1.9],
    opacity: [0, 0.75, 0],
    transition: {
      delay: 0.55 + i * 0.12,
      duration: 0.85,
      times: [0, 0.15, 1],
      ease: 'easeOut',
    },
  }),
};

const ctaV = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.62, ease: EASE_OUT_EXPO } },
};

// Tablet — most elaborate variant in the section. The "awe" moment.
// Multiple properties with per-prop transitions because the entrance is
// one-shot but the boxShadow cycle is infinite.
//
// Entrance tuning (2026-05-23): bumped from a restrained 0.9s "twist
// into place" to a more dramatic 1.5s "rise + tilt + grow" because the
// prior pass read as too fast — eye barely registered the motion before
// it settled. Now the tablet starts further away (scale 0.82, +50px down,
// 22° Y-tilt, 8° X-tilt) and takes ~1.5s + 0.20s delay to settle. Reads
// as a deliberate hero-product arrival rather than an incidental fade.
const tabletV = {
  hidden: {
    opacity: 0,
    scale: 0.82,
    y: 50,
    rotateY: 22,
    rotateX: 8,
    boxShadow: TABLET_REST_SHADOW,
  },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    rotateY: 0,
    rotateX: 0,
    // Keyframe array — boxShadow cycles between rest and peak. Once
    // motion enters `visible`, the boxShadow transition (with infinite
    // repeat, delayed 1.80s) starts the cycle. Before t=1.80, motion
    // holds boxShadow at REST during the entrance transition since the
    // first keyframe IS rest.
    boxShadow: [TABLET_REST_SHADOW, TABLET_PEAK_SHADOW, TABLET_REST_SHADOW],
    transition: {
      // Entrance properties — one-shot, ~1.5s with delay 0.20s
      opacity: { delay: 0.20, duration: 1.5, ease: EASE_OUT_EXPO },
      scale:   { delay: 0.20, duration: 1.5, ease: EASE_OUT_EXPO },
      y:       { delay: 0.20, duration: 1.5, ease: EASE_OUT_EXPO },
      rotateY: { delay: 0.20, duration: 1.5, ease: EASE_OUT_EXPO },
      rotateX: { delay: 0.20, duration: 1.5, ease: EASE_OUT_EXPO },
      // Continuous breath — starts after entrance settles, loops forever.
      // Delay = 0.20 entrance delay + 1.5 duration + 0.10 breathing room.
      boxShadow: {
        duration: 4,
        repeat: Infinity,
        ease: 'easeInOut',
        times: [0, 0.5, 1],
        delay: 1.80,
      },
    },
  },
};

// ─── Static data ────────────────────────────────────────────────────────

const proofPoints = [
  { Icon: TrendingDown, stat: '30-50% less',  label: 'than traditional roofers' },
  { Icon: Clock,        stat: 'Fast quote',    label: 'in ~30 minutes' },
  { Icon: DollarSign,   stat: '$0 down',       label: 'until your roof is installed' },
];

const handleCtaClick = (e) => {
  e.preventDefault();
  openCustomerPortal();
};

// ─── Component ──────────────────────────────────────────────────────────

const TabletDemoSection = () => {
  // Respect `prefers-reduced-motion` (system setting users often enable
  // for vestibular-disorder accessibility or to save battery on mobile).
  // When set, the drift is suppressed — the tablet still arrives with
  // entrance animation + cyan breath but stays still rather than drifting.
  const shouldReduceMotion = useReducedMotion();

  return (
  <motion.section
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.15 }}
    variants={sectionContainerV}
    className="bg-white py-16 lg:py-24 overflow-hidden"
  >
    <div className="max-w-[1440px] mx-auto px-5 lg:px-8">
      {/* Asymmetric desktop grid: right column carries 7/12 of the row so
          the tablet has more room to breathe on screen (previously was
          50/50, which capped the tablet at ~536px). Container widened
          from 1280 → 1440 to push the tablet further right of viewport
          center on wide screens — the tablet now sits visually on the
          right half of the page rather than straddling the center.
          Mobile collapses to a single column — tablet fills the full
          width minus padding. */}
      <div className="grid grid-cols-1 lg:grid-cols-[5fr_7fr] gap-10 lg:gap-12 items-center">

        {/* LEFT column — message + proof points + CTA. Each motion element
            carries its own variant; absolute delays sequence the reading
            order without staggerChildren. */}
        <div className="text-center lg:text-left">
          <motion.p
            variants={eyebrowV}
            className="text-xs font-bold uppercase tracking-[0.2em] mb-4"
            style={{ color: '#07B0E9' }}
          >
            Roofing Reimagined
          </motion.p>
          <motion.h2
            variants={headlineV}
            className="text-3xl sm:text-4xl lg:text-5xl font-black mb-4 leading-[1.1] tracking-tight"
            style={{
              color: '#000150',
              fontFamily: "'Poppins', 'Montserrat', sans-serif",
              letterSpacing: '-0.02em',
            }}
          >
            The Future of Roofing is Here
          </motion.h2>
          <motion.p
            variants={subheadV}
            className="text-gray-500 text-base lg:text-lg leading-relaxed mb-8 max-w-[520px] mx-auto lg:mx-0"
          >
            Design and buy your entire roof online — no sales visits, no guesswork, no pressure.
          </motion.p>

          <ul className="space-y-3 mb-8 inline-block text-left lg:block">
            {proofPoints.map(({ Icon, stat, label }, i) => (
              <motion.li
                key={stat}
                variants={proofItemV}
                custom={i}
                whileHover={{ x: 6, transition: { duration: 0.3, ease: 'easeOut' } }}
                className="flex items-center gap-3 cursor-default"
              >
                {/* Icon-box wrapper — positioned relative so the absolute
                    sonar-ping ring can overlay it. `flex-shrink-0` keeps
                    the 36px box from squishing when the row width changes. */}
                <div className="relative flex-shrink-0 w-9 h-9">
                  {/* Sonar-ping ring — expands and fades on arrival. */}
                  <motion.span
                    variants={proofPingV}
                    custom={i}
                    className="absolute inset-0 rounded-lg pointer-events-none"
                    style={{ border: '2px solid rgba(7, 176, 233, 0.6)' }}
                    aria-hidden="true"
                  />
                  <motion.span
                    variants={proofIconV}
                    custom={i}
                    className="absolute inset-0 flex items-center justify-center rounded-lg"
                    style={{ background: 'rgba(7, 176, 233, 0.10)' }}
                    aria-hidden="true"
                  >
                    <Icon size={18} strokeWidth={2.2} style={{ color: '#07B0E9' }} />
                  </motion.span>
                </div>
                <span className="text-base lg:text-lg leading-tight">
                  <strong className="font-bold" style={{ color: '#000150' }}>{stat}</strong>
                  <span className="text-gray-500"> {label}</span>
                </span>
              </motion.li>
            ))}
          </ul>

          <motion.div variants={ctaV}>
            <a
              href={PORTAL_BASE_URL}
              onClick={handleCtaClick}
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl font-bold text-base whitespace-nowrap transition-all duration-200"
              style={{
                background: 'linear-gradient(135deg, #07B0E9 0%, #0596C7 100%)',
                color: 'white',
                boxShadow: '0 8px 24px rgba(7, 176, 233, 0.5)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(7, 176, 233, 0.65)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(7, 176, 233, 0.5)';
              }}
            >
              <span>Get Your Roof Price</span>
              <ArrowRight size={18} strokeWidth={2.4} aria-hidden="true" />
            </a>
          </motion.div>
        </div>

        {/* RIGHT column — tablet. `perspective` on the wrapper enables the
            3D rotateY/rotateX entrance to render in real 3D space. After
            entrance settles to rotate:0, the tablet sits flat.

            FLOAT WRAPPER (2026-05-24): a separate motion.div around the
            entrance motion.div handles the continuous ambient drift
            (y: 0 → -6 → 0 looping every 7s). Two reasons it's a wrapper
            and not a tweak to the entrance variant:
              1. The entrance variant uses y once (50→0). Mixing the
                 entrance and the looping drift into a single y channel
                 fights itself.
              2. Separation of concerns — the entrance is a one-shot
                 arrival; the drift is forever-ambient. Different
                 timing, different lifecycles.
            The wrapper needs `transformStyle: preserve-3d` so the
            grandchild's rotateY/rotateX still render in the parent's
            perspective space (an intermediate transformed element
            normally flattens its descendants' 3D context).

            Drift is GPU-accelerated translate3d → trivial perf cost on
            mobile. `useReducedMotion()` suppresses the drift entirely
            for users with the system preference set. */}
        <div
          className="flex justify-center lg:justify-end"
          style={{ perspective: '1200px' }}
        >
          <motion.div
            className="w-full max-w-[720px]"
            style={{ transformStyle: 'preserve-3d' }}
            animate={shouldReduceMotion ? undefined : { y: [0, -6, 0] }}
            transition={shouldReduceMotion ? undefined : {
              duration: 7,
              repeat: Infinity,
              ease: 'easeInOut',
              times: [0, 0.5, 1],
            }}
          >
          <motion.div
            variants={tabletV}
            className="relative w-full"
            style={{
              background: '#1a1a1a',
              borderRadius: 24,
              padding: 14,
            }}
          >
            <div
              className="relative w-full overflow-hidden"
              style={{
                aspectRatio: '16 / 9',
                borderRadius: 12,
                background: '#000',
              }}
            >
              {/* Viewport-lazy (2026-07-18): the 2.4MB mp4 no longer
                  downloads on page load. LazyVideo shows the poster in the
                  identical box, attaches the src + starts muted autoplay
                  only when the section nears the viewport, and swaps
                  autoplay for a tap-to-play button under
                  prefers-reduced-motion. */}
              <LazyVideo
                src="/videos/hero-drone.mp4"
                poster="/images/hero-drone-poster.jpg"
                className="absolute inset-0 w-full h-full"
                style={{ objectFit: 'fill' }}
                aria-label="Product demo video"
              />
            </div>
          </motion.div>
          </motion.div>
        </div>

      </div>
    </div>
  </motion.section>
  );
};

export default TabletDemoSection;
